import { v4 as uuidv4 } from 'uuid';
import { FieldType, FilterType } from 'models/enums/fleet-management';
import { RobotModel, RobotScope } from 'components/fleet-management/FleetFiltersBar';
import { DateTime } from 'luxon';
import { toDateString } from '@common/utils/date';

export interface FleetDataFilter {
  id: string;
  field: string;
  type: FilterType;
  values: (number | string)[];
}
/**
 * How the date window was picked. A preset is relative to today, so a saved dashboard or a link
 * holding one rolls forward with the calendar; a custom window keeps its two dates.
 */
export enum DatePeriod {
  CUSTOM = 'CUSTOM',
  LAST_4_WEEKS = 'LAST_4_WEEKS',
  LAST_12_WEEKS = 'LAST_12_WEEKS',
}

export type DatePreset = Exclude<DatePeriod, DatePeriod.CUSTOM>;

export const DATE_PRESETS: DatePreset[] = [DatePeriod.LAST_4_WEEKS, DatePeriod.LAST_12_WEEKS];

export const DEFAULT_DATE_PERIOD: DatePreset = DatePeriod.LAST_4_WEEKS;

/** Days a preset covers, today included: four weeks, and twelve. */
const PRESET_DAYS: Record<DatePreset, number> = {
  [DatePeriod.LAST_4_WEEKS]: 28,
  [DatePeriod.LAST_12_WEEKS]: 84,
};

export const isDatePreset = (value: unknown): value is DatePreset =>
  DATE_PRESETS.includes(value as DatePreset);

/** The window a preset means today, as `YYYY-MM-DD` days ending today. */
export const getPresetRange = (period: DatePreset) => {
  const end = DateTime.now().startOf('day');
  const start = end.minus({ days: PRESET_DAYS[period] - 1 });
  return { start: toDateString(start), end: toDateString(end) };
};

export interface FleetDashboardFilters {
  dealers: string[];
  dealersCustomers: string[];
  tags: string[];
  countries: string[];
  model: RobotModel;
  robotScope: RobotScope;
  period: DatePeriod;
  start: string;
  end: string;
}

export const getFilterValueConfig = (filterType: FilterType, fieldType: FieldType) => {
  switch (fieldType) {
    case FieldType.BOOL:
      return {
        inputs: 1,
        type: FieldType.BOOL,
      };

    case FieldType.STRING_LIST:
      return {
        inputs: 1,
        type: FieldType.STRING,
      };

    case FieldType.DATE:
      if (
        [
          FilterType.EXACT,
          FilterType.GT,
          FilterType.LT,
          FilterType.YEAR,
          FilterType.MONTH,
          FilterType.DAY,
        ].includes(filterType)
      ) {
        return {
          inputs: 1,
          type: FieldType.DATE,
        };
      } else if (filterType === FilterType.RANGE) {
        return {
          inputs: 2,
          type: FieldType.DATE,
        };
      } else if (filterType === FilterType.IS_NULL) {
        return {
          inputs: 1,
          type: FieldType.BOOL,
        };
      }
      break;

    case FieldType.NUMBER:
      if (
        [FilterType.EXACT, FilterType.GT, FilterType.GTE, FilterType.LT, FilterType.LTE].includes(
          filterType,
        )
      ) {
        return {
          inputs: 1,
          type: FieldType.NUMBER,
        };
      } else if (filterType === FilterType.RANGE) {
        return {
          inputs: 2,
          type: FieldType.NUMBER,
        };
      } else if (filterType === FilterType.IN) {
        return {
          inputs: -1,
          type: FieldType.NUMBER,
        };
      } else if (filterType === FilterType.IS_NULL) {
        return {
          inputs: 1,
          type: FieldType.BOOL,
        };
      }
      break;
    case FieldType.STRING:
      if (
        [
          FilterType.EXACT,
          FilterType.I_EXACT,
          FilterType.CONTAINS,
          FilterType.I_CONTAINS,
          FilterType.STARTS_WITH,
          FilterType.ENDS_WIDTH,
          FilterType.REGEX,
          FilterType.I_REGEX,
        ].includes(filterType)
      ) {
        return {
          inputs: 1,
          type: FieldType.STRING,
        };
      } else if (filterType === FilterType.IN) {
        return {
          inputs: -1,
          type: FieldType.STRING,
        };
      } else if (filterType === FilterType.IS_NULL) {
        return {
          inputs: 1,
          type: FieldType.BOOL,
        };
      }
      break;

    default:
      break;
  }
  return null;
};

// EXACT = "EXACT",
//   I_EXACT = "I_EXACT",
//   CONTAINS = "CONTAINS",
//   I_CONTAINS = "I_CONTAINS",
//   STARTS_WITH = "STARTS_WITH",
//   ENDS_WIDTH = "ENDS_WIDTH",
//   REGEX = "REGEX",
//   I_REGEX = "I_REGEX",
//   IN = "IN",
//   IS_NULL = "IS_NULL",
//   GT = "GT",
//   GTE = "GTE",
//   LT = "LT",
//   LTE = "LTE",
//   RANGE = "RANGE",
//   YEAR = "YEAR",
//   MONTH = "MONTH",
//   DAY = "DAY",

export const getDefaultFilters = (): FleetDashboardFilters => {
  const range = getPresetRange(DEFAULT_DATE_PERIOD);

  return {
    dealers: [],
    dealersCustomers: [],
    tags: [],
    countries: [],
    model: RobotModel.ALL,
    robotScope: RobotScope.CUSTOMER,
    period: DEFAULT_DATE_PERIOD,
    start: range.start,
    end: range.end,
  };
};

export const fleetDataFiltersToDashboardFilters = (
  datasetFilters: FleetDataFilter[],
): FleetDashboardFilters => {
  const rawPeriod = datasetFilters.find((f) => f.field === 'period')?.values?.[0];
  const rawStart = datasetFilters.find((f) => f.field === 'start')?.values?.[0];
  const rawEnd = datasetFilters.find((f) => f.field === 'end')?.values?.[0];

  // A preset is worked out again from today. Filters saved before presets existed carry only
  // their dates, so those read as a custom window; with no dates at all the default preset holds.
  let window: { period: DatePeriod; start: string; end: string };
  if (isDatePreset(rawPeriod)) {
    window = { period: rawPeriod, ...getPresetRange(rawPeriod) };
  } else if (typeof rawStart === 'string' && typeof rawEnd === 'string') {
    window = { period: DatePeriod.CUSTOM, start: rawStart, end: rawEnd };
  } else {
    window = { period: DEFAULT_DATE_PERIOD, ...getPresetRange(DEFAULT_DATE_PERIOD) };
  }

  return {
    dealers: datasetFilters
      .filter((f) => f.field === 'dealer_id')
      .flatMap((f) => f.values.map(String)),
    dealersCustomers: datasetFilters
      .filter((f) => f.field === 'customer_id')
      .flatMap((f) => f.values.map(String)),
    tags: datasetFilters.filter((f) => f.field === 'tags').flatMap((f) => f.values.map(String)),
    countries: datasetFilters
      .filter((f) => f.field === 'country')
      .flatMap((f) => f.values.map(String)),
    model:
      (datasetFilters.find((f) => f.field === 'model')?.values?.[0] as RobotModel) ??
      RobotModel.ALL,
    robotScope:
      datasetFilters.find((f) => f.field === 'robot_scope')?.values?.[0] === RobotScope.ALL
        ? RobotScope.ALL
        : RobotScope.CUSTOMER,
    ...window,
  };
};

export const dashboardFiltersToFleetDataFilters = (
  filters: FleetDashboardFilters,
): FleetDataFilter[] => {
  const datasetFilters: FleetDataFilter[] = [];

  // No model has a period field, so the backend passes over it; it is kept for the saved view.
  datasetFilters.push({
    id: uuidv4(),
    field: 'period',
    type: FilterType.EXACT,
    values: [filters.period],
  });
  datasetFilters.push({
    id: uuidv4(),
    field: 'start',
    type: FilterType.EXACT,
    values: [filters.start],
  });
  datasetFilters.push({
    id: uuidv4(),
    field: 'end',
    type: FilterType.EXACT,
    values: [filters.end],
  });

  if (filters.dealers.length > 0) {
    datasetFilters.push({
      id: uuidv4(),
      field: 'dealer_id',
      type: FilterType.IN,
      values: filters.dealers,
    });
  }
  if (filters.dealersCustomers.length > 0) {
    datasetFilters.push({
      id: uuidv4(),
      field: 'customer_id',
      type: FilterType.IN,
      values: filters.dealersCustomers,
    });
  }
  if (filters.tags.length > 0) {
    datasetFilters.push({ id: uuidv4(), field: 'tags', type: FilterType.IN, values: filters.tags });
  }
  if (filters.countries.length > 0) {
    datasetFilters.push({
      id: uuidv4(),
      field: 'country',
      type: FilterType.IN,
      values: filters.countries,
    });
  }
  if (filters.model !== RobotModel.ALL) {
    datasetFilters.push({
      id: uuidv4(),
      field: 'model',
      type: FilterType.EXACT,
      values: [filters.model],
    });
  }
  // Customer held robots are the backend's default, so only widening it is sent.
  if (filters.robotScope === RobotScope.ALL) {
    datasetFilters.push({
      id: uuidv4(),
      field: 'robot_scope',
      type: FilterType.EXACT,
      values: [RobotScope.ALL],
    });
  }

  return datasetFilters;
};
